import { useMemo } from 'react';
import {
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import { AxiosError } from 'axios';

import { getCurrentSearchCountry, getCurrentSearchLang } from '@models/CountriesAndLanguages';
import { DEFAULT_REFETCH_INTERVAL } from '@modules/layouts/hooks/utils';
import { sanitize } from '@utils/filterUtils';

import useQueryCacheKey from './useQueryCacheKey';

export type DataTransformer<T> = (result?: T) => T | undefined;

type DataHandlers<TQueryFnData = unknown> = {
  /**
   * Function to transform fetched data
   */
  dataTransformer?: DataTransformer<TQueryFnData>;
  /**
   * Callback function to run after load success
   */
  onSuccess?: (result: TQueryFnData) => void;
  /** Whether to sanitize the response. Set to false to preserve null-valued properties. */
  sanitizeResponse?: boolean;
};

/**
 * Options for useLoad hook
 */
export type LoadOptions<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UndefinedInitialDataOptions<TQueryFnData, TError, TData | undefined, TQueryKey>,
  'queryFn' | 'queryKey'
> &
  DataHandlers<TQueryFnData> & {
    isTicker?: boolean;
    refetchOnCountryChange?: boolean;
    refetchOnDisableCache?: boolean;
    refetchOnSearchLanguageChange?: boolean;
  };

export const handleLoadFn = <TQueryFnData = unknown>(
  loadFunc: () => Promise<ApiResponse<TQueryFnData | undefined>>,
  {
    onSuccess,
    dataTransformer,
    sanitizeResponse = true,
  }: DataHandlers<TQueryFnData> | undefined = {}
) =>
  loadFunc().then(({ originalError, data, ok }) => {
    if (originalError) throw originalError;
    if (!ok) throw new Error();

    const responseData = sanitizeResponse ? sanitize(data) : data;
    const res = dataTransformer ? dataTransformer(responseData) : responseData;
    if (res && onSuccess) onSuccess(res);
    return res as TQueryFnData;
  });

/**
 * Custom hook to load data with react-query and additional options
 * @template TQueryFnData - Type of data returned by the load function
 * @template TError - Type of error returned by the query
 * @template TData - Type of data returned by the query
 * @param loadFn - Function to load data
 * @param queryKey - Unique key for the query
 * @param options - Additional options for the query
 * @returns Query result object
 */
const useLoad = <
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  loadFn: () => Promise<ApiResponse<TQueryFnData | undefined>>,
  queryKey: TQueryKey,
  options?: LoadOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData | undefined, TError> => {
  const {
    onSuccess,
    dataTransformer,
    isTicker,
    refetchOnCountryChange = true,
    refetchOnDisableCache = true,
    refetchOnSearchLanguageChange = true,
    sanitizeResponse,
    ...rest
  } = options || {};

  const queryCacheKey = useQueryCacheKey(refetchOnDisableCache);

  const queryFn: QueryFunction<TQueryFnData, TQueryKey> = () =>
    handleLoadFn(loadFn, { onSuccess, dataTransformer, sanitizeResponse });

  const initCountry = useMemo(() => getCurrentSearchCountry(), []);
  const initSearchLanguage = useMemo(() => getCurrentSearchCountry(), []);

  // Localization string to trigger refetches on country or language change
  const localization = JSON.stringify({
    country: refetchOnCountryChange ? getCurrentSearchCountry() : initCountry,
    searchLang: refetchOnSearchLanguageChange ? getCurrentSearchLang() : initSearchLanguage,
  });

  // Combined query key with localization and cache keys
  const qk = useMemo(
    () => [...Array.from(queryKey), localization, ...queryCacheKey] as unknown as TQueryKey,
    [localization, queryKey, queryCacheKey]
  );

  return useQuery({
    queryFn,
    queryKey: qk,
    refetchInterval: isTicker ? DEFAULT_REFETCH_INTERVAL : undefined,
    ...rest,
  });
};

export default useLoad;
